import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Plus, 
  Copy, 
  History, 
  Archive,
  Filter,
  Search,
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  MessageSquare,
  RefreshCw,
  Phone
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const MatchAdmin = ({ candidatiProp, agenzieProp }) => {
  const [matches, setMatches] = useState([]);
  const [filteredMatches, setFilteredMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    agency_id: '',
    candidate_id: '',
    updated_from: '',
    zona: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchHistory, setMatchHistory] = useState([]);
  // Use props if available, otherwise load from API
  const [candidati, setCandidati] = useState(candidatiProp || []);
  const [agenzie, setAgenzie] = useState(agenzieProp || []);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [zoneList, setZoneList] = useState([]);

  // Update candidati/agenzie when props change
  useEffect(() => {
    console.log('[MatchAdmin] Props changed - candidatiProp:', candidatiProp?.length || 0);
    console.log('[MatchAdmin] Props changed - agenzieProp:', agenzieProp?.length || 0);
    
    if (candidatiProp && candidatiProp.length > 0) {
      console.log('[MatchAdmin] ✅ Using candidati from props:', candidatiProp.length);
      setCandidati(candidatiProp);
    } else {
      console.log('[MatchAdmin] ⚠️ candidatiProp is empty or undefined');
    }
    
    if (agenzieProp && agenzieProp.length > 0) {
      console.log('[MatchAdmin] ✅ Using agenzie from props:', agenzieProp.length);
      setAgenzie(agenzieProp);
    } else {
      console.log('[MatchAdmin] ⚠️ agenzieProp is empty or undefined');
    }
  }, [candidatiProp, agenzieProp]);

  // Carica dati iniziali
  useEffect(() => {
    loadMatches();
    loadStats();
    loadSuggestions();
    loadZoneList();
    // Only load from API if props not provided
    if (!candidatiProp || candidatiProp.length === 0) {
      loadCandidati();
    }
    if (!agenzieProp || agenzieProp.length === 0) {
      loadAgenzie();
    }
  }, []);
  const loadZoneList = async () => {
    try {
      const response = await fetch(`${API}/agenzie/zone-distinte`);
      if (response.ok) {
        const data = await response.json();
        setZoneList(data);
        console.log('[MatchAdmin] zoneList caricata:', data);
      } else {
        console.error('[MatchAdmin] Errore response zone-distinte:', response.status);
      }
    } catch (error) {
      console.error('Errore caricamento zone distinte:', error);
    }
  };

  // Applica filtri quando cambiano
  useEffect(() => {
    applyFilters();
  }, [matches, filters, searchTerm, candidati, agenzie]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await fetch(`${API}/matches?${queryParams.toString()}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const matchesData = await response.json();
        setMatches(matchesData);
      }
    } catch (error) {
      console.error('Errore nel caricamento matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/matches/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const statsData = await response.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Errore nel caricamento stats:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      const response = await fetch(`${API}/matches/suggestions?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        console.log('✅ Suggerimenti caricati:', data.suggestions.length);
      }
    } catch (error) {
      console.error('Errore nel caricamento suggerimenti:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const loadCandidati = async () => {
    console.log('[MatchAdmin] 🔄 START Loading candidati from:', `${API}/admin/candidati`);
    try {
      const response = await fetch(`${API}/admin/candidati`, {
        headers: getAuthHeaders()
      });
      console.log('[MatchAdmin] Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[MatchAdmin] ✅ Candidati loaded:', data.length, 'items', data);
        setCandidati(data || []);
      } else {
        console.error('[MatchAdmin] ❌ Failed to load candidati:', response.status);
        setCandidati([]);
      }
    } catch (error) {
      console.error('[MatchAdmin] ❌ Errore nel caricamento candidati:', error);
      setCandidati([]);
    }
  };

  const loadAgenzie = async () => {
    console.log('[MatchAdmin] 🔄 START Loading agenzie from:', `${API}/admin/agenzie`);
    try {
      const response = await fetch(`${API}/admin/agenzie`, {
        headers: getAuthHeaders()
      });
      console.log('[MatchAdmin] Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('[MatchAdmin] ✅ Agenzie loaded:', data.length, 'items', data);
        setAgenzie(data || []);
      } else {
        console.error('[MatchAdmin] ❌ Failed to load agenzie:', response.status);
        setAgenzie([]);
      }
    } catch (error) {
      console.error('[MatchAdmin] ❌ Errore nel caricamento agenzie:', error);
      setAgenzie([]);
    }
  };

  const applyFilters = () => {
    let filtered = matches;

    // Filtro per testo (nome candidato o agenzia)
    if (searchTerm) {
      filtered = filtered.filter(match => 
        match.candidate_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.agency_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro per zona (match locale basato sui dati disponibili)
    if (filters.zona) {
      const zonaFiltro = filters.zona.toLowerCase().replace(/\s+/g, '');
      filtered = filtered.filter(match => {
        // Cerca la zona nei nomi o nei dati del match
        const candidateName = (match.candidate_name || '').toLowerCase();
        const agencyName = (match.agency_name || '').toLowerCase();
        // Se abbiamo i dati completi, filtra per zona
        const candidato = candidati.find(c => c.id === match.candidate_id);
        const agenzia = agenzie.find(a => a.id === match.agency_id);
        
        if (candidato) {
          const zonaCandidato = (candidato.zona_preferenza_lavorativa || '').toLowerCase().replace(/\s+/g, '');
          if (zonaCandidato === zonaFiltro) return true;
        }
        if (agenzia) {
          const zoneAgenzia = Array.isArray(agenzia.zone_di_interesse) 
            ? agenzia.zone_di_interesse 
            : [agenzia.zone_di_interesse];
          if (zoneAgenzia.some(z => (z || '').toLowerCase().replace(/\s+/g, '') === zonaFiltro)) return true;
        }
        return false;
      });
    }

    setFilteredMatches(filtered);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Ricarica matches con nuovi filtri
    setTimeout(loadMatches, 100);
  };

  const createMatch = async (candidateId, agencyId, score = null) => {
    try {
      console.log('🔵 [CREATE MATCH] Starting creation...');
      console.log('🔵 [CREATE MATCH] Candidate ID:', candidateId);
      console.log('🔵 [CREATE MATCH] Agency ID:', agencyId);
      console.log('🔵 [CREATE MATCH] Score:', score);
      
      const requestBody = {
        candidate_id: candidateId,
        agency_id: agencyId,
        score: score,
        source: 'admin'
      };
      
      console.log('🔵 [CREATE MATCH] Request body:', JSON.stringify(requestBody, null, 2));
      console.log('🔵 [CREATE MATCH] API URL:', `${API}/matches`);
      
      const response = await fetch(`${API}/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('🔵 [CREATE MATCH] Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [CREATE MATCH] Success:', data);
        await loadMatches();
        await loadStats();
        await loadSuggestions(); // Reload suggestions after creating match
        setShowCreateModal(false);
        alert('Match creato con successo!');
      } else {
        const error = await response.json();
        console.error('❌ [CREATE MATCH] Error response:', error);
        alert(`Errore nella creazione match: ${error.detail || JSON.stringify(error)}`);
      }
    } catch (error) {
      console.error('❌ [CREATE MATCH] Exception:', error);
      alert('Errore nella creazione match: ' + error.message);
    }
  };

  const createMatchFromSuggestion = async (suggestion) => {
    const confirmed = window.confirm(
      `Creare match tra ${suggestion.candidate_name} e ${suggestion.agency_name}?\n\n` +
      `Score: ${suggestion.compatibility_score}%\n` +
      `Motivi: ${suggestion.match_reasons.join(', ')}`
    );
    
    if (confirmed) {
      // Rimuovi immediatamente il suggerimento dalla lista locale
      setSuggestions(prev => prev.filter(s => 
        !(s.candidate_id === suggestion.candidate_id && s.agency_id === suggestion.agency_id)
      ));
      
      await createMatch(
        suggestion.candidate_id,
        suggestion.agency_id,
        suggestion.compatibility_score
      );
    }
  };

  const viewMatchHistory = async (match) => {
    try {
      const response = await fetch(`${API}/matches/${match.id}/history`);
      if (response.ok) {
        const history = await response.json();
        setMatchHistory(history);
        setSelectedMatch(match);
        setShowHistoryModal(true);
      }
    } catch (error) {
      console.error('Errore caricamento cronologia:', error);
    }
  };

  const archiveMatch = async (matchId) => {
    if (!confirm('Sei sicuro di voler archiviare questo match?')) return;

    try {
      const response = await fetch(`${API}/matches/${matchId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'archiviato'
        })
      });

      if (response.ok) {
        await loadMatches();
        await loadStats();
        alert('Match archiviato con successo!');
      }
    } catch (error) {
      console.error('Errore archiviazione match:', error);
      alert('Errore nell\'archiviazione match');
    }
  };

  const createMockData = async () => {
    if (!confirm('Creare dati mock di esempio per testing?')) return;

    try {
      const response = await fetch(`${API}/matches/create-mock-data`, {
        method: 'POST'
      });

      if (response.ok) {
        await loadMatches();
        await loadStats();
        alert('Dati mock creati con successo!');
      }
    } catch (error) {
      console.error('Errore creazione mock data:', error);
      alert('Errore nella creazione mock data');
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'in_attesa_contatto': { color: 'bg-yellow-100 text-yellow-800', text: 'In attesa' },
      'chiamato': { color: 'bg-blue-100 text-blue-800', text: 'Chiamato' },
      'colloquio_fissato': { color: 'bg-green-100 text-green-800', text: 'Colloquio' },
      'inserito': { color: 'bg-purple-100 text-purple-800', text: 'Inserito' },
      'non_interessato': { color: 'bg-red-100 text-red-800', text: 'Non interessato' },
      'archiviato': { color: 'bg-gray-100 text-gray-800', text: 'Archiviato' }
    };
    
    const statusInfo = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return <Badge className={statusInfo.color}>{statusInfo.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const calculateSuccessRate = (total, success) => {
    return total > 0 ? Math.round((success / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento gestione matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header KPI Dashboard - Compatto */}
      <div className="grid grid-cols-5 gap-2">
        <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-bold">{stats.total_matches || 0}</div>
            <div className="text-xs opacity-90">Match</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-bold">{calculateSuccessRate(stats.total_matches, stats.contacted)}%</div>
            <div className="text-xs opacity-90">Contattati</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-bold">{calculateSuccessRate(stats.contacted, stats.interviews_scheduled)}%</div>
            <div className="text-xs opacity-90">Colloqui</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white border-0">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-bold">{calculateSuccessRate(stats.interviews_scheduled, stats.inserted)}%</div>
            <div className="text-xs opacity-90">Inseriti</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white border-0">
          <CardContent className="p-2 text-center">
            <div className="text-xl font-bold">{calculateSuccessRate(stats.total_matches, stats.not_interested)}%</div>
            <div className="text-xs opacity-90">Rifiuti</div>
          </CardContent>
        </Card>
      </div>

      {/* Suggerimenti Automatici - Sezione Compatta */}
      {showSuggestions && (
        <Card className="border-green-200">
          <CardHeader className="py-3">
            <CardTitle className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-600" />
                Suggerimenti ({suggestions.length})
              </div>
              <Button
                onClick={() => setShowSuggestions(false)}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                Chiudi
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            {loadingSuggestions ? (
              <div className="text-center py-2 text-gray-500 text-sm">Caricamento...</div>
            ) : suggestions.length === 0 ? (
              <div className="text-center py-2 text-gray-500 text-sm">
                ✅ Tutti i match compatibili sono già stati creati!
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:shadow-sm transition-all text-sm"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 text-xs">
                        <Users className="w-3 h-3 text-blue-600 flex-shrink-0" />
                        <span className="font-medium truncate">{suggestion.candidate_name}</span>
                        <span className="text-gray-400">→</span>
                        <Building2 className="w-3 h-3 text-purple-600 flex-shrink-0" />
                        <span className="font-medium truncate">{suggestion.agency_name}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Badge className="bg-green-100 text-green-700 border-0 text-xs py-0">
                          {suggestion.compatibility_score}%
                        </Badge>
                        <span className="text-xs text-gray-500 truncate">📍 {suggestion.candidate_zone}</span>
                        {suggestion.candidate_zone === suggestion.agency_zone && suggestion.candidate_zone !== 'N/A' && (
                          <Badge className="bg-orange-100 text-orange-700 border-0 text-xs py-0">
                            🎯
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => createMatchFromSuggestion(suggestion)}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white rounded-lg ml-2 text-xs py-1 px-2"
                    >
                      +
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Azioni e Ricerca - Compatto */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        {/* Azioni */}
        <div className="flex flex-wrap gap-1">
          <Button
            onClick={() => setShowCreateModal(true)}
            size="sm"
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg flex items-center gap-1 text-xs"
          >
            <Plus className="w-3 h-3" />
            Crea Match
          </Button>
          
          <Button
            onClick={() => {
              loadMatches();
              loadStats();
            }}
            variant="outline"
            size="sm"
            className="rounded-lg flex items-center gap-1 text-xs"
          >
            <RefreshCw className="w-3 h-3" />
            Aggiorna
          </Button>
          
          <Button
            onClick={() => {
              loadCandidati();
              loadAgenzie();
              loadSuggestions();
            }}
            variant="outline"
            size="sm"
            className="rounded-lg flex items-center gap-1 text-xs border-orange-400 text-orange-600"
          >
            <RefreshCw className="w-3 h-3" />
            Dati ({candidati.length}C/{agenzie.length}A)
          </Button>
          
          <Button
            onClick={() => setShowSuggestions(!showSuggestions)}
            size="sm"
            className={`rounded-lg flex items-center gap-1 text-xs ${showSuggestions ? 'bg-green-600 text-white' : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'}`}
          >
            <TrendingUp className="w-3 h-3" />
            Suggerimenti ({suggestions.length})
          </Button>
        </div>

        {/* Ricerca */}
        <div className="relative w-48">
          <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-7 pr-2 py-1.5 text-xs border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Filtri - Compatti */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-gray-50 rounded-xl">
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Stato</label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full p-1.5 border rounded-lg text-xs"
          >
            <option value="">Tutti</option>
            <option value="in_attesa_contatto">In attesa</option>
            <option value="chiamato">Chiamato</option>
            <option value="colloquio_fissato">Colloquio</option>
            <option value="inserito">Inserito</option>
            <option value="non_interessato">Non interessato</option>
            <option value="archiviato">Archiviato</option>
          </select>
        </div>
        
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Zona/Città</label>
          <select
            value={filters.zona}
            onChange={(e) => handleFilterChange('zona', e.target.value)}
            className="w-full p-1.5 border rounded-lg text-xs"
          >
            <option value="">Tutte le zone</option>
            {zoneList.map((z) => (
              <option key={z.zona} value={z.zona}>
                {z.zona} ({z.count})
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Agenzia</label>
          <select
            value={filters.agency_id}
            onChange={(e) => handleFilterChange('agency_id', e.target.value)}
            className="w-full p-1.5 border rounded-lg text-xs"
          >
            <option value="">Tutte</option>
            {agenzie.map((agenzia) => (
              <option key={agenzia.id} value={agenzia.id}>
                {agenzia.nome_agenzia}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="text-xs text-gray-600 mb-1 block">Data da</label>
          <input
            type="date"
            value={filters.updated_from}
            onChange={(e) => handleFilterChange('updated_from', e.target.value)}
            className="w-full p-1.5 border rounded-lg text-xs"
          />
        </div>
        
        <div className="flex items-end">
          <Button
            onClick={() => {
              setFilters({ status: '', agency_id: '', candidate_id: '', updated_from: '', zona: '' });
              setSearchTerm('');
              setTimeout(loadMatches, 100);
            }}
            variant="outline"
            size="sm"
            className="w-full rounded-lg text-xs"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Tabella Matches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Gestione Match ({filteredMatches.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredMatches.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50 text-gray-400" />
              <p className="text-gray-500">Nessun match trovato per i filtri applicati.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-semibold text-gray-700">Candidato</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Agenzia</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Stato</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Ultima Azione</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Note</th>
                    <th className="text-left p-3 font-semibold text-gray-700">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.map((match) => (
                    <tr key={match.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                            {match.candidate_name?.charAt(0) || 'C'}
                          </div>
                          <div>
                            <div className="font-semibold text-sm">{match.candidate_name}</div>
                            <div className="text-xs text-gray-500">Score: {match.score || 'N/A'}%</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="font-semibold text-sm">{match.agency_name}</div>
                        <div className="text-xs text-gray-500">
                          Creato: {formatDate(match.created_at)}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        {getStatusBadge(match.status)}
                      </td>
                      
                      <td className="p-3">
                        <div className="text-sm">
                          {match.last_action_by?.role && (
                            <>
                              <div className="font-semibold capitalize">{match.last_action_by.role}</div>
                              <div className="text-xs text-gray-500">
                                {formatDate(match.updated_at)}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="space-y-1 text-xs">
                          {match.agency_note && (
                            <div className="bg-blue-50 p-2 rounded text-blue-700">
                              <strong>Agenzia:</strong> {match.agency_note.substring(0, 50)}...
                            </div>
                          )}
                          {match.candidate_note && (
                            <div className="bg-green-50 p-2 rounded text-green-700">
                              <strong>Candidato:</strong> {match.candidate_note.substring(0, 50)}...
                            </div>
                          )}
                        </div>
                      </td>
                      
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            onClick={() => viewMatchHistory(match)}
                            variant="outline"
                            size="sm"
                            className="p-2"
                            title="Vedi cronologia"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          
                          {match.status !== 'archiviato' && (
                            <Button
                              onClick={() => archiveMatch(match.id)}
                              variant="outline"
                              size="sm"
                              className="p-2 text-red-600 hover:text-red-700"
                              title="Archivia"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Crea Match */}
      {showCreateModal && (
        <CreateMatchModal
          key={`match-modal-${Date.now()}`}
          candidatiProp={candidati}
          agenzieProp={agenzie}
          zoneList={zoneList}
          onClose={() => setShowCreateModal(false)}
          onCreate={createMatch}
        />
      )}

      {/* Modal Cronologia */}
      {showHistoryModal && selectedMatch && (
        <MatchHistoryModal
          match={selectedMatch}
          history={matchHistory}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

// Componente Modal per Creare Match
// zoneList: [{zona: 'Firenze', count: 28}, ...]
const CreateMatchModal = ({ candidatiProp, agenzieProp, zoneList = [], onClose, onCreate }) => {

  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [selectedAgency, setSelectedAgency] = useState('');
  const [score, setScore] = useState('85');
  const [selectedZona, setSelectedZona] = useState('');
  const [candidati, setCandidati] = useState(candidatiProp || []);
  const [agenzie, setAgenzie] = useState(agenzieProp || []);
  const [loading, setLoading] = useState(false);

  // Safe array wrappers
  const safeCandidati = Array.isArray(candidati) ? candidati : [];
  const safeAgenzie = Array.isArray(agenzie) ? agenzie : [];

  // Helper per auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Carica dati solo se non passati come props
  useEffect(() => {
    const loadData = async () => {
      if ((!candidatiProp || candidatiProp.length === 0) || (!agenzieProp || agenzieProp.length === 0)) {
        setLoading(true);
        try {
          if (!candidatiProp || candidatiProp.length === 0) {
            const candResponse = await fetch(`${API}/admin/candidati`, {
              headers: getAuthHeaders()
            });
            if (candResponse.ok) {
              const candData = await candResponse.json();
              setCandidati(candData || []);
            }
          }
          if (!agenzieProp || agenzieProp.length === 0) {
            const agResponse = await fetch(`${API}/admin/agenzie`, {
              headers: getAuthHeaders()
            });
            if (agResponse.ok) {
              const agData = await agResponse.json();
              setAgenzie(agData || []);
            }
          }
        } catch (error) {
          console.error('Errore caricamento dati:', error);
        }
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Funzione per normalizzare zona (rimuove spazi extra e lowercase)
  const normalizeZona = (zona) => {
    if (!zona) return '';
    return zona.toString().trim().toLowerCase();
  };

  // Funzione per verificare se una zona è valida (non N/A, non vuota)
  const isZonaValida = (zona) => {
    if (!zona) return false;
    const normalized = normalizeZona(zona);
    return normalized !== '' && normalized !== 'n/a' && normalized !== 'na' && normalized !== '-';
  };

  // Filtra candidati per zona selezionata
  const candidatiFiltrati = safeCandidati.filter(candidato => {
    const zonaCandidato = normalizeZona(candidato.zona_preferenza_lavorativa);
    
    // Se è selezionata una zona, filtra per quella zona (escludi sempre N/A)
    if (selectedZona) {
      if (!isZonaValida(candidato.zona_preferenza_lavorativa)) return false;
      const zonaSelezionata = normalizeZona(selectedZona);
      return zonaCandidato.includes(zonaSelezionata) || zonaSelezionata.includes(zonaCandidato);
    }
    
    // Se nessuna zona selezionata, mostra tutti (anche senza zona)
    return true;
  });

  // Filtra agenzie per zona selezionata
  const agenzieFiltrate = safeAgenzie.filter(agenzia => {
    // Se è selezionata una zona, filtra per quella zona
    if (selectedZona) {
      const zonaSelezionata = normalizeZona(selectedZona);
      
      // Controlla zone_di_interesse (può essere stringa con più zone separate da virgola, o array)
      let zoneAgenzia = [];
      if (Array.isArray(agenzia.zone_di_interesse)) {
        zoneAgenzia = agenzia.zone_di_interesse.filter(isZonaValida).map(z => normalizeZona(z));
      } else if (agenzia.zone_di_interesse && isZonaValida(agenzia.zone_di_interesse)) {
        // Potrebbe essere una stringa con virgole: "CREMONA, MILANO, BERGAMO"
        const zoneStr = agenzia.zone_di_interesse.toString();
        zoneAgenzia = zoneStr.split(',').filter(z => isZonaValida(z)).map(z => normalizeZona(z));
      }
      
      // Controlla anche il campo 'zona' se esiste e valido
      if (agenzia.zona && isZonaValida(agenzia.zona)) {
        zoneAgenzia.push(normalizeZona(agenzia.zona));
      }
      
      // Controlla anche 'citta' se esiste e valido
      if (agenzia.citta && isZonaValida(agenzia.citta)) {
        zoneAgenzia.push(normalizeZona(agenzia.citta));
      }
      
      return zoneAgenzia.some(z => z.includes(zonaSelezionata) || zonaSelezionata.includes(z));
    }
    
    // Se nessuna zona selezionata, mostra tutte
    return true;
  });

  const handleSubmit = () => {
    if (!selectedCandidate || !selectedAgency) {
      alert('Seleziona candidato e agenzia');
      return;
    }
    onCreate(selectedCandidate, selectedAgency, parseInt(score) || null);
  };

  // Reset selezioni quando cambia zona
  useEffect(() => {
    setSelectedCandidate('');
    setSelectedAgency('');
  }, [selectedZona]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4">Crea Nuovo Match</h3>
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Caricamento dati...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(safeCandidati.length === 0 || safeAgenzie.length === 0) && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-sm text-yellow-800">
                ⚠️ Attenzione: {safeCandidati.length === 0 ? 'Nessun candidato disponibile. ' : ''}
                {safeAgenzie.length === 0 ? 'Nessuna agenzia disponibile. ' : ''}
              </div>
            )}

            {/* Dropdown zona */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Filtra per Zona</label>
              <select
                value={selectedZona}
                onChange={e => setSelectedZona(e.target.value)}
                className="w-full p-3 border rounded-xl"
              >
                <option value="">Tutte le zone ({safeCandidati.length} cand. / {safeAgenzie.length} ag.)</option>
                {zoneList.map(z => (
                  <option key={z.zona} value={z.zona}>
                    {z.zona} ({z.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Candidato */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Candidato</label>
              <select
                value={selectedCandidate}
                onChange={(e) => setSelectedCandidate(e.target.value)}
                className="w-full p-3 border rounded-xl"
              >
                <option value="">Seleziona candidato...</option>
                {candidatiFiltrati.map((candidato) => (
                  <option key={candidato.id} value={candidato.id}>
                    {candidato.nome} {candidato.cognome} - {candidato.zona_preferenza_lavorativa || 'N/A'}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                {candidatiFiltrati.length} candidati disponibili
              </div>
            </div>
          
            {/* Agenzia */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Agenzia</label>
              <select
                value={selectedAgency}
                onChange={(e) => setSelectedAgency(e.target.value)}
                className="w-full p-3 border rounded-xl"
              >
                <option value="">Seleziona agenzia...</option>
                {agenzieFiltrate.map((agenzia) => (
                  <option key={agenzia.id} value={agenzia.id}>
                    {agenzia.nome_agenzia} - {Array.isArray(agenzia.zone_di_interesse) ? agenzia.zone_di_interesse.join(', ') : (agenzia.zone_di_interesse || agenzia.zona || 'N/A')}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-500 mt-1">
                {agenzieFiltrate.length} agenzie disponibili
              </div>
            </div>
          
            {/* Score */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Score di compatibilità (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={score}
                onChange={(e) => setScore(e.target.value)}
                className="w-full p-3 border rounded-xl"
                placeholder="85"
              />
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={onClose}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
                disabled={!selectedCandidate || !selectedAgency}
              >
                Crea Match
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Modal per Cronologia Match
const MatchHistoryModal = ({ match, history, onClose }) => {
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('it-IT');
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'create': <Plus className="w-4 h-4" />,
      'chiamato': <Phone className="w-4 h-4" />,
      'colloquio_fissato': <Calendar className="w-4 h-4" />,
      'inserito': <CheckCircle className="w-4 h-4" />,
      'non_interessato': <XCircle className="w-4 h-4" />,
      'note': <MessageSquare className="w-4 h-4" />,
      'archiviato': <Archive className="w-4 h-4" />
    };
    
    return iconMap[action] || <Clock className="w-4 h-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          Cronologia Match: {match.candidate_name} ↔ {match.agency_name}
        </h3>
        
        <div className="space-y-3">
          {history.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nessuna cronologia disponibile.</p>
          ) : (
            history.map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                  {getActionIcon(item.action)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold capitalize">{item.action.replace('_', ' ')}</span>
                    <Badge className="bg-gray-200 text-gray-700 text-xs">
                      {item.by_role}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    {formatTimestamp(item.timestamp)}
                  </div>
                  
                  {item.payload && Object.keys(item.payload).length > 0 && (
                    <div className="mt-2 p-2 bg-white rounded text-xs">
                      <pre className="text-gray-600">
                        {JSON.stringify(item.payload, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <Button
          onClick={onClose}
          className="w-full mt-6 rounded-xl"
        >
          Chiudi
        </Button>
      </div>
    </div>
  );
};

export default MatchAdmin;